import React from "react";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Stack, Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatCents, useHandleOpportunity, useTaxOpportunities } from "../../../api/fiscalite";

const SEVERITY_COLOR: Record<string, "error" | "warning" | "info" | "default"> = {
  HIGH: "error",
  MEDIUM: "warning",
  LOW: "info",
  INFO: "default",
};

interface Props {
  fleetId: number;
}

/**
 * Optimisation findings.
 *
 * Every card shows the evidence that triggered it. A recommendation a fleet
 * manager cannot audit is one they will not act on — and, if it turns out
 * wrong, one they will hold the product responsible for.
 *
 * Dismissals are permanent: the detector re-runs nightly and must never
 * resurrect a finding the customer has already judged.
 */
export const Opportunites: React.FC<Props> = ({ fleetId }) => {
  const { t } = useTranslation();
  const { opportunities, totalImpactCents, isLoading } = useTaxOpportunities(fleetId);
  const handle = useHandleOpportunity(fleetId);

  if (isLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>;
  }

  if (opportunities.length === 0) {
    return (
      <Alert severity="success">
        {t("tax.noOpportunities", "Aucune opportunite detectee sur le parc avec les regles en vigueur.")}
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      {totalImpactCents > 0 && (
        <Card>
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              {t("tax.estimatedImpact", "Impact annuel estime")}
            </Typography>
            <Typography variant="h4" fontWeight={700}>{formatCents(totalImpactCents)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t(
                "tax.impactCaveat",
                "Somme des impacts chiffrables. Ordre de grandeur destine a prioriser, pas un gain garanti."
              )}
            </Typography>
          </CardContent>
        </Card>
      )}

      {opportunities.map((opportunity) => (
        <Card key={opportunity.id}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Chip size="small" color={SEVERITY_COLOR[opportunity.severity]} label={opportunity.severity} />
                  <Typography variant="subtitle1" fontWeight={600}>{opportunity.title}</Typography>
                  {opportunity.vehicle_id && (
                    <Chip size="small" variant="outlined" label={`#${opportunity.vehicle_id}`} />
                  )}
                </Stack>

                <Typography variant="body2" sx={{ mb: 1 }}>{opportunity.message}</Typography>

                {opportunity.action && (
                  <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
                    → {opportunity.action}
                  </Typography>
                )}

                {/* The evidence is shown, not hidden behind a tooltip. */}
                {opportunity.evidence && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                    {JSON.stringify((opportunity.evidence as any).context ?? opportunity.evidence)}
                  </Typography>
                )}
              </Box>

              <Stack spacing={1} alignItems="flex-end">
                {opportunity.estimated_impact_cents !== null && (
                  <Typography variant="h6" fontWeight={700}>
                    {formatCents(opportunity.estimated_impact_cents)}
                  </Typography>
                )}
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small" variant="outlined"
                    onClick={() => handle.mutate({ id: opportunity.id, status: "ACKNOWLEDGED" })}
                  >
                    {t("tax.acknowledge", "Pris en compte")}
                  </Button>
                  <Button
                    size="small" color="inherit"
                    onClick={() => handle.mutate({ id: opportunity.id, status: "DISMISSED" })}
                  >
                    {t("tax.dismiss", "Ignorer")}
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};
